import React from 'react';
import { Card } from '../components/ui/card';
import { Shield, Lock, Eye, Database, UserCheck, Globe } from 'lucide-react';

export const PrivacyPage = () => {
  const lastUpdated = "January 1, 2026";
  
  return (
    <div className="min-h-screen bg-background pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
                Privacy Policy
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Your privacy is important to us. This policy explains how Urban Oasis Cascais, operated by Urban Oasis LLC ("we," "us," or "our"), handles your personal information in compliance with the General Data Protection Regulation (GDPR) and applicable privacy laws.
          </p>
        </div>

        {/* Key Points Summary */}
        <Card className="p-6 mb-8 bg-primary/5 border-primary/20">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-primary" />
            Privacy at a Glance
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              We do not collect personal information unless you voluntarily provide it
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              Information is only used to facilitate bookings through Propr or your preferred platform
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              We do not sell, trade, or share your personal data with third parties
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              You have full rights to access, modify, or delete your data at any time
            </li>
          </ul>
        </Card>

        {/* Sections */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              1. Information We Collect
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  We only collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Submit a booking inquiry through our contact form</li>
                  <li>Contact us directly via our property management partner, Propr</li>
                  <li>Make a reservation through third-party platforms (Airbnb, VRBO, Booking.com, etc.)</li>
                </ul>
                <p className="font-medium text-foreground">Information collected may include:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Name and contact information (email address)</li>
                  <li>Booking dates and guest count</li>
                  <li>Special requests or messages you provide</li>
                </ul>
                <p className="text-sm bg-muted/50 p-3 rounded-lg mt-4">
                  <strong>Note:</strong> We do not use cookies for tracking, analytics, or advertising purposes. This website does not employ any tracking technologies.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              2. How We Use Your Information
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Your personal information is used exclusively for the following purposes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>To respond to your booking inquiries and facilitate reservations</li>
                  <li>To communicate important information about your stay</li>
                  <li>To provide customer support through our property manager, Propr</li>
                  <li>To comply with legal obligations</li>
                </ul>
                <p className="font-medium text-foreground mt-4">We will never:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Sell your personal information to third parties</li>
                  <li>Use your information for marketing without explicit consent</li>
                  <li>Share your data with advertisers or data brokers</li>
                </ul>
              </div>
            </Card>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              3. Third-Party Booking Platforms
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  If you choose to book through third-party platforms such as Airbnb, VRBO, or Booking.com, your information is collected and processed according to their respective privacy policies. We encourage you to review:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Airbnb Privacy Policy: airbnb.com/terms/privacy_policy</li>
                  <li>VRBO Privacy Policy: vrbo.com/legal/privacy-policy</li>
                  <li>Booking.com Privacy Policy: booking.com/content/privacy.html</li>
                </ul>
                <p>
                  We receive only the information necessary to fulfill your booking from these platforms.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" />
              4. Your Rights Under GDPR
            </h2>
            <Card className="p-6 bg-card border-border">
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Under the General Data Protection Regulation (GDPR), you have the following rights:
                </p>
                <div className="grid gap-3 mt-4">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Right of Access:</strong> Request a copy of the personal data we hold about you
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Right to Rectification:</strong> Request correction of inaccurate or incomplete data
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Right to Restrict Processing:</strong> Request limitation of how we use your data
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Right to Data Portability:</strong> Receive your data in a structured, commonly used format
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <strong className="text-foreground">Right to Object:</strong> Object to processing of your personal data
                  </div>
                </div>
                <p className="mt-4">
                  To exercise any of these rights, please contact us through our contact form or reach out to your Propr Host.
                </p>
              </div>
            </Card>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              5. Data Retention
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. Booking inquiry data is retained for a maximum of 24 months after your last interaction with us, unless you request earlier deletion.
              </p>
            </Card>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              6. Data Security
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </Card>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              7. Children's Privacy
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us immediately.
              </p>
            </Card>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              8. Changes to This Policy
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
              </p>
            </Card>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-display font-semibold text-foreground mb-4">
              9. Contact Us
            </h2>
            <Card className="p-6 bg-card border-border">
              <p className="text-muted-foreground">
                If you have questions about this Privacy Policy or wish to exercise your data protection rights, please contact us through our <a href="/contact" className="text-primary hover:underline">contact form</a> or reach out to your Propr Host for assistance.
              </p>
              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Data Controller:</strong><br />
                  Urban Oasis LLC<br />
                  Property managed by <a href="https://propr.pt" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Propr</a>
                </p>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
};
